import { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  ArrowRightCircle,
  Briefcase,
  Calendar,
  ClipboardCheck,
  FileText,
  GraduationCap,
  History,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Send,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Video,
  type LucideIcon,
} from "lucide-react"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/modules/shared/ui/components/sheet"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/modules/shared/ui/components/tabs"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Button } from "@/modules/shared/ui/components/button"
import { Skeleton } from "@/modules/shared/ui/components/skeleton"
import { useCandidateProfile } from "../../hooks/useCandidateProfile"
import { useOpenCandidateCv } from "../../hooks/useOpenCandidateCv"
import type {
  CandidateHistoryEvent,
  CandidateIdVerificationSummary,
  CandidateProfile,
  CandidateProfileApplication,
} from "../../dtos/Candidate"
import { StageBadge } from "./CandidatesTable"

interface Props {
  candidateId: string | null
  onClose: () => void
}

function formatDate(value: string | null | undefined) {
  if (!value) return "—"
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function SectionCard({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  )
}

function DetailRow({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right break-words min-w-0">{value}</span>
    </div>
  )
}

function IdVerificationCard({
  verification,
}: {
  verification: CandidateIdVerificationSummary
}) {
  if (!verification.hasBeenRun) {
    return (
      <SectionCard title="ID verification">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <ShieldAlert className="size-4 shrink-0" />
          No ID verification has been run for this candidate.
        </div>
      </SectionCard>
    )
  }

  const fullName = [verification.firstName, verification.surname]
    .filter(Boolean)
    .join(" ")

  return (
    <SectionCard title="ID verification">
      <div className="flex items-center gap-2">
        {verification.isVerified ? (
          <ShieldCheck className="size-4 shrink-0 text-emerald-600" />
        ) : (
          <ShieldAlert className="size-4 shrink-0 text-destructive" />
        )}
        <Badge variant={verification.isVerified ? "default" : "destructive"}>
          {verification.isVerified ? "Verified" : (verification.status ?? "Failed")}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {formatDate(verification.runAt)}
          {verification.timesRun > 1 ? ` · run ${verification.timesRun} times` : ""}
        </span>
      </div>

      <div className="space-y-1.5">
        <DetailRow label="ID number" value={verification.maskedIdNumber ?? "—"} />
        {verification.isVerified && (
          <>
            <DetailRow label="Name on record" value={fullName || "—"} />
            <DetailRow
              label="Date of birth"
              value={formatDate(verification.dateOfBirth)}
            />
            <DetailRow label="Gender" value={verification.gender ?? "—"} />
            <DetailRow
              label="Deceased"
              value={verification.isDeceased ? "Yes" : "No"}
            />
            <DetailRow
              label="Adverse indicator"
              value={
                verification.hasAdverseIndicator ? (
                  <span className="text-destructive">Yes</span>
                ) : (
                  "No"
                )
              }
            />
          </>
        )}
      </div>
    </SectionCard>
  )
}

function ApplicationsList({
  applications,
  onOpen,
}: {
  applications: CandidateProfileApplication[]
  onOpen: (applicationId: string) => void
}) {
  if (applications.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6">
        This candidate has not applied for any vacancies in your organisation.
      </p>
    )
  }

  return (
    <ul className="divide-y divide-border rounded-lg border border-border bg-card">
      {applications.map((app) => (
        <li
          key={app.applicationId}
          className="flex flex-wrap items-center justify-between gap-3 p-4"
        >
          <div className="min-w-0 space-y-1">
            <div className="font-medium truncate">{app.vacancyTitle}</div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Calendar className="size-3" />
                Applied {formatDate(app.appliedAt)}
              </span>
              <span>Vacancy: {app.vacancyStatus}</span>
              {app.matchScore !== null && <span>AI score: {app.matchScore}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <StageBadge stage={app.stage} />
            <Button
              size="sm"
              variant="outline"
              onClick={() => onOpen(app.applicationId)}
            >
              View application
            </Button>
          </div>
        </li>
      ))}
    </ul>
  )
}

const HISTORY_TYPES: Record<
  string,
  { label: string; icon: LucideIcon }
> = {
  applied: { label: "Applications", icon: Send },
  ai_scoring: { label: "AI screening", icon: Sparkles },
  stage: { label: "Stage changes", icon: ArrowRightCircle },
  interview: { label: "Interviews", icon: Video },
  scorecard: { label: "Scorecards", icon: ClipboardCheck },
  comment: { label: "Comments", icon: MessageSquare },
  id_verification: { label: "ID verification", icon: ShieldCheck },
}

const TONE_STYLES: Record<string, string> = {
  success: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
  warning: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
  danger: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
  neutral: "bg-muted text-muted-foreground",
}

function HistoryTimeline({ history }: { history: CandidateHistoryEvent[] }) {
  const [filter, setFilter] = useState<string>("all")

  if (history.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6">No history yet.</p>
    )
  }

  const typesPresent = Object.keys(HISTORY_TYPES).filter((type) =>
    history.some((event) => event.type === type),
  )
  const visible =
    filter === "all" ? history : history.filter((event) => event.type === filter)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        <Button
          size="sm"
          variant={filter === "all" ? "default" : "outline"}
          onClick={() => setFilter("all")}
        >
          All ({history.length})
        </Button>
        {typesPresent.map((type) => (
          <Button
            key={type}
            size="sm"
            variant={filter === type ? "default" : "outline"}
            onClick={() => setFilter(type)}
          >
            {HISTORY_TYPES[type].label} (
            {history.filter((event) => event.type === type).length})
          </Button>
        ))}
      </div>

      <ol className="space-y-4">
        {visible.map((event, index) => {
          const Icon = HISTORY_TYPES[event.type]?.icon ?? History
          return (
            <li
              key={`${event.type}-${event.occurredAt}-${index}`}
              className="flex gap-3"
            >
              <span
                className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full ${TONE_STYLES[event.tone] ?? TONE_STYLES.neutral}`}
              >
                <Icon className="size-4" />
              </span>
              <div className="min-w-0 flex-1 rounded-lg border border-border bg-card px-4 py-3">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <div className="text-sm font-medium">{event.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {formatDateTime(event.occurredAt)}
                  </div>
                </div>
                {event.vacancyTitle && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {event.vacancyTitle}
                  </div>
                )}
                {event.description && (
                  <p className="mt-1.5 text-sm text-muted-foreground whitespace-pre-line break-words">
                    {event.description}
                  </p>
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function ProfileBody({
  profile,
  onOpenApplication,
}: {
  profile: CandidateProfile
  onOpenApplication: (applicationId: string) => void
}) {
  return (
    <div className="grid gap-6 p-6 lg:grid-cols-[minmax(300px,380px)_1fr] items-start">
      <aside className="space-y-4">
        <SectionCard title="Contact">
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 break-all">
              <Mail className="size-4 shrink-0 text-muted-foreground" />
              {profile.email}
            </div>
            {profile.phone && (
              <div className="flex items-center gap-2">
                <Phone className="size-4 shrink-0 text-muted-foreground" />
                {profile.phone}
              </div>
            )}
            {profile.location && (
              <div className="flex items-center gap-2">
                <MapPin className="size-4 shrink-0 text-muted-foreground" />
                {profile.location}
              </div>
            )}
          </div>
        </SectionCard>

        <SectionCard title="Background">
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Briefcase className="size-4 shrink-0 text-muted-foreground" />
              {profile.experienceYears > 0
                ? `${profile.experienceYears} year${profile.experienceYears !== 1 ? "s" : ""} experience`
                : "Experience not recorded"}
            </div>
            <div className="flex items-start gap-2">
              <GraduationCap className="size-4 shrink-0 text-muted-foreground mt-0.5" />
              {profile.education || "Education not recorded"}
            </div>
          </div>
        </SectionCard>

        <SectionCard title={`Skills (${profile.skills.length})`}>
          {profile.skills.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {profile.skills.map((skill) => (
                <Badge key={skill} variant="secondary" className="font-normal">
                  {skill}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No skills recorded.</p>
          )}
        </SectionCard>

        {profile.certifications.length > 0 && (
          <SectionCard title={`Certifications (${profile.certifications.length})`}>
            <div className="flex flex-wrap gap-1.5">
              {profile.certifications.map((cert) => (
                <Badge key={cert} variant="outline" className="font-normal">
                  {cert}
                </Badge>
              ))}
            </div>
          </SectionCard>
        )}

        <IdVerificationCard verification={profile.idVerification} />
      </aside>

      <main className="min-w-0">
        <Tabs defaultValue="applications" className="gap-4">
          <TabsList>
            <TabsTrigger value="applications">
              <Briefcase />
              Vacancies ({profile.applications.length})
            </TabsTrigger>
            <TabsTrigger value="history">
              <History />
              History ({profile.history.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="applications">
            <ApplicationsList
              applications={profile.applications}
              onOpen={onOpenApplication}
            />
          </TabsContent>
          <TabsContent value="history">
            <HistoryTimeline history={profile.history} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  )
}

function ProfileSkeleton() {
  return (
    <div className="grid gap-6 p-6 lg:grid-cols-[minmax(300px,380px)_1fr]">
      <div className="space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28 w-full rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-96 w-full rounded-lg" />
    </div>
  )
}

export function CandidateProfileSheet({ candidateId, onClose }: Props) {
  const navigate = useNavigate()
  const { data: profile, isPending, isError, refetch } =
    useCandidateProfile(candidateId)
  const { openCv, openingCandidateId } = useOpenCandidateCv()

  const handleOpenApplication = (applicationId: string) => {
    navigate(`/recruitment/applications/${applicationId}`)
  }

  const fullName = profile ? `${profile.firstName} ${profile.lastName}`.trim() : ""

  return (
    <Sheet open={!!candidateId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-none sm:w-[94vw] lg:w-[88vw] gap-0 p-0"
      >
        <SheetHeader className="border-b border-border pr-14">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="text-xl truncate">
                {fullName || "Candidate"}
              </SheetTitle>
              <SheetDescription>
                {profile
                  ? `Candidate since ${formatDate(profile.createdAt)}`
                  : "Loading candidate details"}
              </SheetDescription>
            </div>
            {profile?.hasCv && candidateId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void openCv(candidateId)}
                disabled={openingCandidateId === candidateId}
              >
                <FileText className="mr-2 size-4" />
                {profile.cvFileName ?? "View CV"}
              </Button>
            )}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {isPending && candidateId ? (
            <ProfileSkeleton />
          ) : isError || !profile ? (
            <div className="flex flex-col items-center justify-center gap-3 py-24">
              <p className="text-sm text-muted-foreground">
                Could not load this candidate.
              </p>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            </div>
          ) : (
            <ProfileBody
              profile={profile}
              onOpenApplication={handleOpenApplication}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
